/* Copyright (c) 2020-present CloudIO Inc */

import { DBRow } from '@cloudio-saas/datasource-types';
import { emptyFunction } from '@cloudio-saas/statex';
import { BaseProps, PageHeader } from 'componentregistry';
import React, { createContext, ReactNode, useContext, useState } from 'react';
import Two from 'two.js';
import PubSub, { PubSub as PubSubClass } from 'utils/PubSub';
import { SyncPubSub } from 'utils/SyncPubSub';
import { SidebarColors, useSidebarColorSettings } from '../theme/Theme';

const EMPTY_OBJECT = Object.freeze({});
const EMPTY_ARRAY = Object.freeze([]) as unknown as string[];

const RowIndexContext = createContext<number | undefined>(undefined);

const PinnedContext = createContext<boolean>(false);

/**
 * Use this hook to get the current row index of the records being rendered.
 * @example
 * ```
 * const rowIndex = useRowIndex();
 * ```
 * @returns rowIndex - The index of the records being rendered.
 */
function useRowIndex() {
  return useContext(RowIndexContext);
}

function usePinned(): boolean {
  return useContext(PinnedContext);
}

// Static Runtime is to prevent the Context Menu in places like SQL Worksheet
const StaticRuntimeContext = createContext<boolean>(false);

function useStaticRuntime() {
  return useContext(StaticRuntimeContext);
}

// to prevent circular mirrors
const MirrorContext = createContext<string[]>(EMPTY_ARRAY);

function useMirrorContext() {
  return useContext(MirrorContext);
}

function useIsMirrored() {
  return useContext(MirrorContext).length > 0;
}

const ToolbarContext = createContext<boolean>(false);

function useIsInsideToolbar() {
  return useContext(ToolbarContext);
}

const PageRefContext = createContext<{
  pageRef: React.RefObject<HTMLDivElement> | null;
  pageWidth: number;
  pageHeight: number;
}>({ pageRef: null, pageHeight: 0, pageWidth: 0 });

function usePageRefObject() {
  return useContext(PageRefContext);
}

const SVGElementContext = createContext<React.RefObject<SVGElement> | null>(
  null,
);

function useSVGElement() {
  return useContext(SVGElementContext);
}

const PubSubContext = createContext<PubSubClass>(PubSub);

function usePubSub() {
  return useContext(PubSubContext);
}

export interface CaptureEventMap {
  click: { event: React.MouseEvent };
  contextmenu: { event: React.MouseEvent };
  mousedown: { event: React.MouseEvent };
  capture: {
    clientX: number;
    clientY: number;
    label: string;
  };
}
export const CapturePubSubContext = createContext<SyncPubSub<CaptureEventMap>>(
  new SyncPubSub<CaptureEventMap>(),
);

export function useCapturePubSub() {
  return useContext(CapturePubSubContext);
}

export const TwoJSContext = createContext<{
  containerRef: React.RefObject<HTMLDivElement>;
  drawing: boolean;
  imageHeight: number;
  imageWidth: number;
  two: Two | null;
}>({
  containerRef: { current: null },
  drawing: false,
  imageHeight: 0,
  imageWidth: 0,
  two: null,
});

export function useTwoJS() {
  return useContext(TwoJSContext);
}

const SetPopoverAnchorElementContext = createContext<{
  setEl: (el: HTMLOrSVGElement | null) => void;
  onMouseEnter?: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  onMouseLeave?: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
}>({
  setEl: emptyFunction,
  onMouseEnter: emptyFunction,
  onMouseLeave: emptyFunction,
});

function useSetPopoverAnchorElement() {
  return useContext(SetPopoverAnchorElementContext);
}

const TriggerExtraProps = createContext<Record<string, unknown>>(EMPTY_OBJECT);

function useTriggerExtraProps() {
  return useContext(TriggerExtraProps);
}

export interface CollectionRecords {
  [key: string]: Record<string, unknown>;
}

const CollectionRecordContext = createContext<CollectionRecords>(EMPTY_OBJECT);

function useCollectionRecord() {
  return useContext(CollectionRecordContext);
}

const CollectionRowIndexContext = createContext(-1);

function useCollectionRowIndex() {
  return useContext(CollectionRowIndexContext);
}

export interface EmbeddedPageContextProps {
  embedded: boolean;
  onRoot: boolean;
  rootPageId?: string;
  embeddedPageInstId?: string;
}

export const DefaultEmbeddedPageContextProps: EmbeddedPageContextProps = {
  embedded: false,
  onRoot: false,
};

const EmbeddedContext = createContext<EmbeddedPageContextProps>(
  DefaultEmbeddedPageContextProps,
);

function useEmbeddedContext() {
  return useContext(EmbeddedContext);
}

export interface PageHeaderProps
  extends Required<PageHeader.PageHeader>,
    BaseProps<'PageHeader'> {}

export interface PageHeaderContextProps {
  props: PageHeaderProps | null;
  embeddedPageContextProps: EmbeddedPageContextProps;
  ref?: React.Ref<HTMLDivElement>;
}

const PageHeaderContext = createContext<PageHeaderContextProps>({
  props: null,
  embeddedPageContextProps: DefaultEmbeddedPageContextProps,
});

function usePageHeader() {
  return useContext(PageHeaderContext);
}

const SetPageHeaderContext =
  createContext<React.Dispatch<React.SetStateAction<PageHeaderContextProps>>>(
    emptyFunction,
  );

function useSetPageHeader() {
  return useContext(SetPageHeaderContext);
}

export interface TooltipData {
  label?: string;
  record: DBRow<unknown>;
  category?: string;
  value: number;
  hover: boolean;
}
export interface ChartJSTooltipContextProps {
  x: number;
  y: number;
  data: TooltipData[];
  opacity: number;
  ref?: React.RefObject<HTMLDivElement>;
}

export const DefaultChartJSTooltipContextProps: ChartJSTooltipContextProps = {
  x: 0,
  y: 0,
  data: [],
  opacity: 0,
};

export const ChartJSTooltipContext = createContext<ChartJSTooltipContextProps>(
  DefaultChartJSTooltipContextProps,
);

export function useChartJSTooltip() {
  return useContext(ChartJSTooltipContext);
}

export const SetChartJSTooltipContext =
  createContext<
    React.Dispatch<React.SetStateAction<ChartJSTooltipContextProps>>
  >(emptyFunction);

export function useSetChartJSTooltip() {
  return useContext(SetChartJSTooltipContext);
}

const SidebarColorsContext = createContext<SidebarColors>({
  bgColor: '',
  bgDefaultColor: '',
  borderColor: '',
  textColor: '',
});

export function useSidebarColors() {
  return useContext(SidebarColorsContext);
}

export function SidebarColorProvider({
  appUid,
  children,
}: {
  appUid: string;
  children: ReactNode;
}) {
  const colors = useSidebarColorSettings(appUid);
  return (
    <SidebarColorsContext.Provider value={colors}>
      {children}
    </SidebarColorsContext.Provider>
  );
}
declare type Path = (string | number)[];
export interface SaveContextProps {
  contextValue: {
    [key: string]: {
      value: any;
      ds: string;
      path: Path;
      pkKey: 'userName' | 'appUid';
      pkValue: string;
    };
  };
  prevContextValue: {
    [key: string]: {
      value: any;
      ds: string;
      path: Path;
      pkKey: 'userName' | 'appUid';
      pkValue: string;
    };
  };
  setContextValue: (
    key: string,
    value: any,
    ds: string,
    path: Path,
    pkKey: 'userName' | 'appUid',
    pkValue: string,
  ) => void;
  setPrevContextValue: React.Dispatch<
    React.SetStateAction<{
      [key: string]: {
        value: any;
        ds: string;
        path: Path;
        pkKey: 'userName' | 'appUid';
        pkValue: string;
      };
    }>
  >;
}

const DefaultSaveContextProps: SaveContextProps = {
  contextValue: {},
  prevContextValue: {},
  setContextValue: () => {},
  setPrevContextValue: () => {},
};
const SaveContext = createContext<SaveContextProps>(DefaultSaveContextProps);
function useSaveContext() {
  return useContext(SaveContext);
}
export const SaveProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [contextValue, setContextValueState] = useState<{
    [key: string]: {
      value: any;
      ds: string;
      path: Path;
      pkKey: 'userName' | 'appUid';
      pkValue: string;
    };
  }>({});

  const [prevContextValue, setPrevContextValue] = useState<{
    [key: string]: {
      value: any;
      ds: string;
      path: Path;
      pkKey: 'userName' | 'appUid';
      pkValue: string;
    };
  }>({});
  const setContextValue = (
    key: string,
    value: any,
    ds: string,
    path: Path,
    pkKey: 'userName' | 'appUid',
    pkValue: string,
  ) => {
    setContextValueState((prev) => ({
      ...prev,
      [key]: { value, ds, path, pkKey, pkValue },
    }));
  };
  return (
    <SaveContext.Provider
      value={{
        contextValue,
        setContextValue,
        prevContextValue,
        setPrevContextValue,
      }}
    >
      {children}
    </SaveContext.Provider>
  );
};
export {
  CollectionRecordContext,
  CollectionRowIndexContext,
  EmbeddedContext,
  MirrorContext,
  PageHeaderContext,
  PageRefContext,
  PinnedContext,
  PubSubContext,
  RowIndexContext,
  SVGElementContext,
  SetPageHeaderContext,
  SetPopoverAnchorElementContext,
  StaticRuntimeContext,
  ToolbarContext,
  TriggerExtraProps,
  SaveContext,
  useCollectionRecord,
  useCollectionRowIndex,
  useEmbeddedContext,
  useIsInsideToolbar,
  useIsMirrored,
  useMirrorContext,
  usePageHeader,
  usePageRefObject,
  usePinned,
  usePubSub,
  useRowIndex,
  useSVGElement,
  useSetPageHeader,
  useSetPopoverAnchorElement,
  useStaticRuntime,
  useTriggerExtraProps,
  useSaveContext,
};
